import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import DruhPrijmuSelect from 'components/DruhPrijmuSelect';
import RodinnyStavSelect from 'components/RodinnyStavSelect';
import DebtList from 'components/DebtList';
import ZpusobBydleniSelect from 'components/ZpusobBydleniSelect';
import BankAccountsList, { BankAccount } from './components/BankAccountsList';
import { Breadcrumbs, Button, Form, FormInput, PageHeader, PageTitle, Section } from 'modules';
import PropertiesList, { Property } from './components/PropertiesList';
import ChattelsList, { Chattel } from './components/ChattelsList';
import {
  SaveContractOnlineFormDataMutation,
  CreateContractFromOnlineFormMutation,
} from 'queries/ContractOnlineForms/ContractOnlineFormData';
import { notifyFailed, notifySuccess } from 'helpers/notify';
import { prepareCacheData } from 'helpers/data/prepareCacheData';
import ExpensesList, { Expense } from './components/ExpensesList';
import Partner from './components/Partner';
import { TARIFFS } from 'constants/Tariffs';
import { WebsocketActions, WebsocketContext } from 'services/WebsocketContext';
import { useDebouncedCallback } from 'use-debounce';
import { FormScene } from 'constants/FormScene';
import { Trans, useTranslation } from 'react-i18next';
import {
  selectClientAccess,
  selectConsultantAccess,
  selectMeetingModeOffline,
  selectOnlineForm,
  selectOnlineFormContract,
  selectOnlineFormData,
} from 'stores/onlineForm/onlineForm';
import { v4 as uuidv4 } from 'uuid';
import Payment from './components/Payment/Payment';
import { PaymentType } from 'constants/PaymentType';
import { createLifewayConsultant } from 'apis/Lifeway';
import moment from 'moment';

const myUUID = uuidv4();

interface Agreement {
  name: string;
  label: ReactElement;
  validation?: any;
}

const agreementsListObo: Agreement[] = [
  {
    name: 'agreeVop',
    label: (
      <>
        Súhlasím so{' '}
        <a href="/assets/2025-01-01-vseobecne-obchodne-podmienky.pdf" target="_blank">
          Všeobecnými obchodnými podmienkami
        </a>
      </>
    ),
    validation: Yup.bool().oneOf([true], 'Súhlas je povinný!'),
  },
  {
    name: 'agreePovinostPlatby',
    label: (
      <>
        Bol som zoznámený s{' '}
        <a
          href="/assets/2025-01-01-dolezite-upozornenie-pred-odoslanim-objednavky-s-povinnostou-platby.pdf"
          target="_blank"
        >
          dôležitým upozornením pred odoslaním objednávky s povinnosťou platby
        </a>
      </>
    ),
    validation: Yup.bool().oneOf([true], 'Súhlas je povinný!'),
  },
  {
    name: 'agreeZacatekPoskytovaniSluzeb',
    label: (
      <>
        Dávam výslovný súhlas so začatím{' '}
        <a
          href="/assets/2024-11-14-vyslovny-suhlas-so-zacatim-poskytovania-sluzby-pred-uplynutim-lehoty-na-odstupenie-od-zmluvy.pdf"
          target="_blank"
        >
          poskytovania služby pred uplynutím lehoty na odstúpenie od zmluvy
        </a>
      </>
    ),
  },
];

const OnlineFormData = () => {
  const { t } = useTranslation();

  const wsContext = useContext(WebsocketContext);

  const form = useSelector(selectOnlineForm);
  const formData = useSelector(selectOnlineFormData);
  const contract = useSelector(selectOnlineFormContract);
  const isConsultantAccess = useSelector(selectConsultantAccess);
  const isClientAccess = useSelector(selectClientAccess);
  const isMeetingModeOffline = useSelector(selectMeetingModeOffline);

  const [submitting, setSubmitting] = useState(false);
  const [agreements, setAgreements] = useState<Agreement[]>([]);

  const [saveOnlineFormData] = useMutation(SaveContractOnlineFormDataMutation);
  const [createContractFromOnlineForm] = useMutation(CreateContractFromOnlineFormMutation);

  const agreementValidations = agreements
    .filter((a) => a.validation)
    .reduce((acc, agreement) => {
      acc = {
        ...acc,
        [agreement.name]: agreement.validation,
      };
      return acc;
    }, {});

  const validation = Yup.object().shape({
    lastName: Yup.string().nullable().required('Priezvisko je povinné!'),
    firstName: Yup.string().nullable().required('Meno je povinné!'),
    mobil: Yup.string().nullable().required('Mobil je povinný!'),
    email: Yup.string().nullable().required('E-mail je povinný!'),
    maritalStatus: Yup.string().nullable().required('Rodinný stav je povinný!'),
    living: Yup.string().nullable().required('Spôsob bývanie je povinný!'),
    income: Yup.string().nullable().required('Druh príjmu je povinný!'),
    incomeValue: Yup.string().nullable().required('Výška príjmu je povinná!'),
    debts: Yup.array().nullable().min(1, 'Nie je zadaný zoznam dlhov'),
    cislo_op: Yup.string().nullable().required('Číslo občianského preukazu je povinné!'),
    rc: Yup.string().nullable().required('Rodné číslo je povinné!'),
    street: Yup.string().nullable().required('Ulica je povinná!'),
    houseNumber: Yup.string().nullable().required('Súpisné číslo je povinné!'),
    city: Yup.string().nullable().required('Miesto je povinné!'),
    zipCode: Yup.string().nullable().required('PSČ je povinné!').max(6, 'PSČ je príliš dlhé, max. 6 znakov'),
    productId: Yup.string().required('Produkt je povinný!'),
    // productId: Yup.string().test('product', 'Produkt je povinný!', (value: string) => {
    //   return !!tarifList?.some((tarif: GQL.ITariff) => parseInt(value) === tarif.id);
    // }),
    payment: Yup.string().nullable().required('Výška platby je povinná!'),
    paymentDate: Yup.string().nullable().required('Dátum splatnosti platby je povinný!'),
    paymentMethod: Yup.string().nullable().required('Spôsob úhrady je povinný!'),
    // souhlasy
    ...agreementValidations,
  });

  const saveFormData = async () => {
    // uložení dat formuláře
    if (form) {
      await saveOnlineFormData({
        variables: {
          id: form.id,
          data: prepareCacheData({
            ...formData,
            ...formik.values,
          }),
        },
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      lastName: formData?.lastName,
      firstName: formData?.firstName,
      mobil: formData?.mobil,
      email: formData?.email,
      cislo_op: formData?.cislo_op,
      rc: formData?.rc,
      street: formData?.street,
      houseNumber: formData?.houseNumber,
      city: formData?.city,
      zipCode: formData?.zipCode,
      mailingAddress: formData?.mailingAddress || false,
      mailingAddressStreet: formData?.mailingAddressStreet,
      mailingAddressHouseNumber: formData?.mailingAddressHouseNumber,
      mailingAddressCity: formData?.mailingAddressCity,
      mailingAddressZipCode: formData?.mailingAddressZipCode,
      maritalStatus: formData?.maritalStatus,
      children: formData?.children || 0,
      living: formData?.living,
      income: formData?.income,
      incomeValue: formData?.incomeValue || 0,
      productId: formData?.productId,
      doublePayments: formData?.doublePayments || false,
      installments: formData?.installments || false,
      installmentsData: formData?.installmentsData || null,
      installmentsPlan: formData?.installmentsPlan || null,
      payment: formData?.payment,
      paymentDate: formData?.paymentDate || moment().format('Y-MM-DD'),
      paymentMethod: formData?.paymentMethod || PaymentType.TransferToAccount,
      secondPayment: formData?.secondPayment,
      secondPaymentDate: formData?.secondPaymentDate,
      debts: formData?.debts || undefined,
      expensesList: formData?.expensesList || undefined,
      execution: formData?.execution,
      deductionsFromWages: formData?.deductionsFromWages,
      bankAccounts: formData?.bankAccounts,
      bankAccountsList: formData?.bankAccountsList,
      blockedBankAccounts: formData?.blockedBankAccounts,
      properties: formData?.properties,
      propertiesList: formData?.propertiesList,
      chattels: formData?.chattels,
      chattelsList: formData?.chattelsList,
      // 2. klient
      client2: formData?.client2,
      // souhlasy
      agreeVop: false,
      agreePovinostPlatby: false,
      agreeZacatekPoskytovaniSluzeb: false,
    },
    validationSchema: validation,
    validateOnChange: isConsultantAccess,
    validateOnBlur: false,
    onSubmit: async (d: any) => {
      // start indikace
      setSubmitting(true);
      // uložení dat formuláře
      await saveFormData();
      // vytvoření smlouvy
      createContractFromOnlineForm({
        variables: {
          formId: form?.id,
        },
      })
        .then((resultCreateContract: any) => resultCreateContract.data.createContractFromOnlineForm)
        .then(async (result) => {
          // request pro založení registrace v lifeway
          if (d.productId === TARIFFS.OBO_LW) {
            await createLifewayConsultant({
              ...d,
              consultantNumber: contract?.consultant2?.cislo,
              contractId: result?.id,
            });
          }
          // další krok je přesun na výsledek smlouvy
          wsContext.send({
            action: WebsocketActions.STEP_CHANGE,
            value: FormScene.Summary,
          });
          // notifikace
          notifySuccess('Objednávka bola uzavretá');
        })
        .catch(() => notifyFailed('Objednávku sa nepodarilo uložiť, prosím zopakujte akciu neskôr'))
        .finally(() => setSubmitting(false));
    },
  });

  const productId = formik.values.productId;

  // zpožděné odeslání změn do BE
  const saveFormDataDebounced = useDebouncedCallback(() => {
    saveFormData();
  }, 5000);

  // strana oblastního
  // ve formuláři se změnili data, odešlou se na BE
  useEffect(() => {
    if (isConsultantAccess) {
      saveFormDataDebounced();
    }
    // eslint-disable-next-line
  }, [formik.values]);

  // zpožděné odeslání změn do BE
  const handleChangeDebounced = useDebouncedCallback((value) => {
    wsContext?.send({
      action: WebsocketActions.CONTRACT_DATA_CHANGE,
      value,
    });
  }, 200);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    formik.handleChange(e);
    handleChangeDebounced({
      sender: myUUID,
      name,
      value,
    });
  };

  const handleChangeAggreements = (e: any) => {
    const { name, value } = e.target;
    wsContext?.send({
      action: WebsocketActions.CONTRACT_AGREEEMENT_CHECKED,
      value: {
        name,
        checked: value === true,
      },
    });
  };

  const hasClient2 = useMemo((): boolean => {
    return formik?.values?.client2?.finished === true;
    // eslint-disable-next-line
  }, [formik.values]);

  useEffect(() => {
    if (hasClient2 && formik.values?.productId !== TARIFFS.OBO_MA_2023) {
      formik.handleChange({
        target: {
          name: 'productId',
          value: TARIFFS.OBO_MA_2023,
        },
      });
    }
    // eslint-disable-next-line
  }, [formik.values]);

  useEffect(() => {
    if (productId === TARIFFS.OBO_LW) {
      setAgreements([]);
    } else {
      setAgreements(agreementsListObo);
    }
  }, [productId]);

  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.CONTRACT_DATA_CHANGE, (data) => {
      if (data.sender !== myUUID) {
        if ((formik.values as any)[data.name] !== data.value) {
          formik.setFieldValue(data.name, data.value);
        }
      }
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.CONTRACT_DATA_CHANGE);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.CONTRACT_AGREEEMENT_CHECKED, (data) => {
      formik.setFieldValue(data.name, data.checked);
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.CLIENT_AGREEMENT_CHECKED);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader>
        <Breadcrumbs>
          <Breadcrumbs.Item to="/" active={false}>
            <Trans>ONLINE_FORMS.DETAIL</Trans>
          </Breadcrumbs.Item>
        </Breadcrumbs>
      </PageHeader>

      <Container className="m-0">
        <PageTitle title={t('ONLINE_FORMS.DETAIL')} />

        <Form onSubmit={formik.handleSubmit}>
          <Section className="mb-3">
            <Row>
              <Col md={6}>
                <FormInput
                  name="lastName"
                  label={'Priezvisko'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.lastName}
                  error={formik.errors?.lastName || ''}
                  onChange={handleChange}
                />
              </Col>
              <Col md={6}>
                <FormInput
                  name="firstName"
                  label={'Meno'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.firstName}
                  error={formik.errors?.firstName}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3} sm={6}>
                <FormInput
                  name="mobil"
                  label={'Mobil'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.mobil}
                  error={formik.errors?.mobil}
                  onChange={handleChange}
                />
              </Col>
              <Col md={3} sm={6}>
                <FormInput
                  name="email"
                  label={'E-mail'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.email}
                  error={formik.errors?.email}
                  onChange={handleChange}
                />
              </Col>
              <Col md={3} sm={6}>
                <FormInput
                  name="cislo_op"
                  label={'Číslo OP'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.cislo_op}
                  error={formik.errors?.cislo_op}
                  onChange={handleChange}
                />
              </Col>
              <Col md={3} sm={6}>
                <FormInput
                  name="rc"
                  label={'Rodné číslo'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.rc}
                  error={formik.errors?.rc}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4} xs={8}>
                <FormInput
                  name="street"
                  label={'Ulica'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.street}
                  error={formik.errors?.street}
                  onChange={handleChange}
                />
              </Col>
              <Col md={2} xs={4}>
                <FormInput
                  name="houseNumber"
                  label={'Súpisné č.'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.houseNumber}
                  error={formik.errors?.houseNumber}
                  onChange={handleChange}
                />
              </Col>
              <Col md={4} xs={8}>
                <FormInput
                  name="city"
                  label={'Mesto'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.city}
                  error={formik.errors?.city}
                  onChange={handleChange}
                />
              </Col>
              <Col md={2} xs={4}>
                <FormInput
                  name="zipCode"
                  label={'PSČ'}
                  required
                  readOnly={isClientAccess}
                  value={formik.values?.zipCode}
                  error={formik.errors?.zipCode}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="mailingAddress"
                  label={'Korešpondenčná adresa'}
                  disabled={isClientAccess}
                  checked={formik.values?.mailingAddress}
                  error={formik.errors?.mailingAddress}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            {formik.values?.mailingAddress === true && (
              <Row>
                <Col md={4} xs={8}>
                  <FormInput
                    name="mailingAddressStreet"
                    label={'Koresp. ulica'}
                    readOnly={isClientAccess}
                    value={formik.values?.mailingAddressStreet}
                    error={formik.errors?.mailingAddressStreet}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={2} xs={4}>
                  <FormInput
                    name="mailingAddressHouseNumber"
                    label={'Koresp. súpisné č.'}
                    readOnly={isClientAccess}
                    value={formik.values?.mailingAddressHouseNumber}
                    error={formik.errors?.mailingAddressHouseNumber}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={4} xs={8}>
                  <FormInput
                    name="mailingAddressCity"
                    label={'Koresp. mesto'}
                    readOnly={isClientAccess}
                    value={formik.values?.mailingAddressCity}
                    error={formik.errors?.mailingAddressCity}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={2} xs={4}>
                  <FormInput
                    name="mailingAddressZipCode"
                    label={'Koresp. PSČ'}
                    readOnly={isClientAccess}
                    value={formik.values?.mailingAddressZipCode}
                    error={formik.errors?.mailingAddressZipCode}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            )}

            <Row>
              <Col sm={6}>
                <RodinnyStavSelect
                  name="maritalStatus"
                  label={'Rodinný stav'}
                  required
                  disabled={isClientAccess}
                  value={formik.values?.maritalStatus}
                  error={formik.errors?.maritalStatus}
                  onChange={handleChange}
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  type="number"
                  name="children"
                  label={'Počet detí'}
                  min={0}
                  readOnly={isClientAccess}
                  value={formik.values?.children}
                  error={formik.errors?.children}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={4}>
                <ZpusobBydleniSelect
                  name="living"
                  label={'Bývanie'}
                  required
                  disabled={isClientAccess}
                  value={formik.values?.living}
                  error={formik.errors?.living}
                  onChange={handleChange}
                />
              </Col>
              <Col sm={5}>
                <DruhPrijmuSelect
                  name="income"
                  label={'Príjem'}
                  required
                  disabled={isClientAccess}
                  value={formik.values?.income}
                  error={formik.errors?.income}
                  onChange={handleChange}
                />
              </Col>
              <Col sm={3}>
                <FormInput
                  type="number"
                  name="incomeValue"
                  label={'Výška príjmu'}
                  min={0}
                  disabled={isClientAccess}
                  value={formik.values?.incomeValue}
                  error={formik.errors?.incomeValue}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            {form && contract && (
              <Partner
                isViewMode={isClientAccess}
                form={form}
                contract={contract}
                values={formik.values.client2}
                onChange={handleChange}
              />
            )}
          </Section>

          <Section className="mb-3">
            <ExpensesList
              name="expensesList"
              list={(formik.values.expensesList as Expense[]) || []}
              readOnly={isClientAccess}
              onChange={handleChange}
            />
          </Section>

          <Section className="mb-3">
            <DebtList
              name="debts"
              list={formik.values?.debts || undefined}
              required
              readOnly={isClientAccess}
              error={formik.errors.debts as any}
              onChange={handleChange}
            />
          </Section>

          <Section className="mb-3">
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="execution"
                  label={'Exekúcie'}
                  disabled={isClientAccess}
                  checked={formik.values.execution || false}
                  error={formik.errors.execution}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="deductionsFromWages"
                  label={'Zrážky zo mzdy (dôchodku) dávky'}
                  disabled={isClientAccess}
                  checked={formik.values.deductionsFromWages || false}
                  error={formik.errors.deductionsFromWages}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="bankAccounts"
                  label={'Účet v banke'}
                  disabled={isClientAccess}
                  checked={formik.values.bankAccounts || false}
                  error={formik.errors.bankAccounts}
                  onChange={handleChange}
                />
                {formik.values.bankAccounts === true && (
                  <BankAccountsList
                    name="bankAccountsList"
                    list={(formik.values.bankAccountsList as BankAccount[]) || []}
                    readOnly={isClientAccess}
                    onChange={handleChange}
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="blockedBankAccounts"
                  label={'Zablokovaný účet'}
                  disabled={isClientAccess}
                  checked={formik.values.blockedBankAccounts || false}
                  error={formik.errors.blockedBankAccounts}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="properties"
                  label={'Nehnuteľnosť'}
                  disabled={isClientAccess}
                  checked={formik.values.properties || false}
                  error={formik.errors.properties}
                  onChange={handleChange}
                />
                {formik.values.properties === true && (
                  <PropertiesList
                    name="propertiesList"
                    list={(formik.values?.propertiesList as Property[]) || []}
                    readOnly={isClientAccess}
                    onChange={handleChange}
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput
                  type="checkbox"
                  name="chattels"
                  label={'Hmotné veci'}
                  disabled={isClientAccess}
                  checked={formik.values.chattels || false}
                  error={formik.errors.chattels}
                  onChange={handleChange}
                />
                {formik.values.chattels === true && (
                  <ChattelsList
                    name="chattelsList"
                    list={(formik.values?.chattelsList as Chattel[]) || []}
                    readOnly={isClientAccess}
                    onChange={handleChange}
                  />
                )}
              </Col>
            </Row>
          </Section>

          <Section className="mb-3">
            <Payment
              values={formik.values}
              errors={formik.errors}
              hasClient2={hasClient2}
              setFieldValue={formik.setFieldValue}
              onChange={handleChange}
            />
          </Section>

          <div className="mb-3">
            {agreements.map((agreement) => (
              <Row key={agreement.name}>
                <Col>
                  <FormInput
                    type={'checkbox'}
                    name={agreement.name}
                    checked={(formik.values as any)[agreement.name]}
                    error={(formik.errors as any)[agreement.name] || ''}
                    label={agreement.label}
                    disabled={isConsultantAccess && !isMeetingModeOffline}
                    onChange={handleChangeAggreements}
                  />
                </Col>
              </Row>
            ))}
          </div>

          <Row>
            <Col className="text-right">
              {/* {(isConsultantAccess || isOffline()) && <GoBackButton formId={form.form_id} status="" />} */}
              <Button
                variant="primary"
                type="submit"
                disabled={!isMeetingModeOffline && (isConsultantAccess || submitting)}
                loading={submitting}
              >
                Potvrdzujem, údaje sú správne
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </>
  );
};

export default OnlineFormData;
